import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Box, Paper, Stack, Typography } from '@mui/material';
import DemoBanner from './DemoBanner';
import IssueInsightCard from './IssueInsightCard';
import * as aiClient from './aiClient';
import NotificationBell, { BELL_SLOT_ID } from '../motion/NotificationBell';

// Founder decision 2026-10-17: render nothing (not even a locked/disabled state)
// unless the org's ai_assistant entitlement is exactly true — same gate as
// ResolutionButtons.jsx/ChatPanel.jsx.
export default function AiNotifications() {
  const aiAssistant = useSelector((s) => s.entitlements?.ai_assistant === true);
  const currentOrgId = useSelector((s) => s.organizations?.currentOrgId ?? null);

  const [notifications, setNotifications] = useState([]);
  const [bellSlot, setBellSlot] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const listRef = useRef(null);

  useEffect(() => { setBellSlot(document.getElementById(BELL_SLOT_ID)); }, []);

  useEffect(() => {
    // Org switch (or the flag turning off) must drop the previous org's list
    // immediately — never leave it rendered under the new org — and a late
    // response from a since-superseded org/entitlement state must be ignored.
    let cancelled = false;
    setNotifications([]);
    setLoaded(false);

    if (!aiAssistant) {
      return () => {
        cancelled = true;
      };
    }

    aiClient
      .getNotifications()
      .then((result) => {
        if (cancelled) return;
        setNotifications(result || []);
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setNotifications([]);
      });

    return () => {
      cancelled = true;
    };
  }, [aiAssistant, currentOrgId]);

  if (!aiAssistant) return null;

  const openPanel = () => {
    listRef.current?.scrollIntoView?.({ block: 'nearest' });
    listRef.current?.focus();
  };

  return (
    <>
      {bellSlot && createPortal(
        <NotificationBell count={loaded ? notifications.length : null} latestTitle={notifications[0]?.title} onOpen={openPanel} />,
        bellSlot
      )}
      <Stack data-testid="ai-notifications-list" spacing={1} ref={listRef} tabIndex={-1} aria-label="Notifications">
      <DemoBanner />
      {notifications.map((n) => (
        <Paper key={n.id} variant="outlined" data-testid={`ai-notification-${n.id}`} sx={{ p: 1.5 }}>
          <Typography variant="subtitle2">{n.title}</Typography>
          <Typography variant="body2">{n.body}</Typography>
          {n.issueInsight && (
            <Box data-testid="ai-notification-insight">
              <IssueInsightCard insight={n.issueInsight} />
            </Box>
          )}
        </Paper>
      ))}
      </Stack>
    </>
  );
}
